import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * Counts how many rows (children of `ref` marked with `data-fitting-item`) fit inside `ref`.
 * When not all of them fit - or `forceReserve` is set because there are more items than rendered -
 * `reservedHeight` is kept free at the bottom (e.g. for a "+N more" button).
 * Rows that don't fit must stay mounted (hidden, out of flow) so their height can still be measured.
 */
export const useFittingItemsCount = (
    ref: RefObject<HTMLElement>,
    itemsCount: number,
    reservedHeight: number,
    forceReserve = false
) => {
    const [count, setCount] = useState(itemsCount);

    useLayoutEffect(() => {
        const container = ref.current;
        if (!container) return;

        const calculate = () => {
            const rows = Array.from(container.querySelectorAll<HTMLElement>(":scope > [data-fitting-item]"));
            const heights = rows.map((row) => row.offsetHeight);
            const available = container.clientHeight;
            const totalHeight = heights.reduce((sum, height) => sum + height, 0);

            if (!forceReserve && totalHeight <= available) {
                setCount(rows.length);
                return;
            }

            const budget = available - reservedHeight;
            let used = 0;
            let fitting = 0;
            for (const height of heights) {
                if (used + height > budget) break;
                used += height;
                fitting++;
            }
            setCount(fitting);
        };

        calculate();

        // Recalculate in the next frame - updating synchronously inside the observer callback
        // triggers "ResizeObserver loop" errors
        let frame = 0;
        const resizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(calculate);
        });
        resizeObserver.observe(container);
        container.querySelectorAll(":scope > [data-fitting-item]").forEach((row) => resizeObserver.observe(row));

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
        };
    }, [ref, itemsCount, reservedHeight, forceReserve]);

    return Math.min(count, itemsCount);
};
